import { useMemo } from 'react';
import DocumentHeader from '@components/DocumentHeader';
import track, { useTracking } from 'react-tracking';
import If from '@components/If/If';
import { TileLink } from '@components/Tile';
import {
  ROUTE_INVESTING_IDEA_HOME,
  ROUTE_INVESTING_IDEA_DISCOVERY_PAGE_HOME,
  ROUTE_COMMUNITY_IDEA,
} from '@/constants/routes';
import { getGridViewCoverFromImageUrl } from '@/utilities/coverImages';
import {
  getCloudflareImageUrl,
  getResponsiveImage,
  // getResponsiveImage,
} from '@/hooks/useCloudflareImage';
import useUser from '@/hooks/useUser';
import { Badge, Box, Text, Grid } from '@simplywallst/ui-core';
import { Tile } from '@/components/TileNew';
import { Snowflake } from '@simplywallst/ui-charts/Snowflake';
import { CompanyIcon } from '@/components/CompanyIcon';
import ScrollLoader from '@/components/ScrollLoader';
import { LoadingSkeleton } from './LoadingSkeleton';
import { useInfiniteInvestingIdeas } from '../../hooks/useInfiniteInvestingIdeas';
import type { TypeSlug } from '../../hooks/useInfiniteInvestingIdeas/types';
import { isMarketSupported } from '@utilities/markets';
import { parseSnowflakePointsFromRules } from '@/pages/DiscoverPageV1/components/BasicHeading/components/NewBasicHeading/utils';
import { getTopCompaniesIconList } from './utils';

const getRouteType = (typeSlug: TypeSlug) => {
  if (typeSlug === 'discover-page-v2')
    return ROUTE_INVESTING_IDEA_DISCOVERY_PAGE_HOME;
  if (typeSlug === 'narrative-collection') return ROUTE_COMMUNITY_IDEA;
  return ROUTE_INVESTING_IDEA_HOME;
};

const ExploreIdeas = () => {
  const { trackEvent } = useTracking();
  const { data: user } = useUser();
  const rawMarket = user.defaultMarketISO || user.countryISO;
  const market = isMarketSupported(rawMarket) ? rawMarket : 'global';

  const {
    data: dataGql,
    fetchNextPage,
    hasNextPage,
    isFetching,
  } = useInfiniteInvestingIdeas({
    limit: 12,
    market,
  });

  const investingIdeas = useMemo(
    () => dataGql?.pages.flat().filter(Boolean) || [],
    [dataGql]
  );

  return (
    <>
      <DocumentHeader title="Discover - Investing Ideas" allowCrawlers={true} />
      <If match={investingIdeas.length !== 0}>
        <ScrollLoader
          handleLoadRequest={() => {
            if (hasNextPage) {
              fetchNextPage();
            }
          }}
        >
          {() => {
            return (
              <Box
                marginLeft="x2"
                marginRight="x2"
                marginLeftLg="x0"
                marginRightLg="x0"
              >
                <Grid
                  gap="24px"
                  templateColumnsMd="repeat(2, 1fr)"
                  templateColumnsLg="repeat(3, 1fr)"
                  width="100%"
                  data-cy-id="tile-view"
                >
                  {investingIdeas.map((idea, index) => {
                    const { name, slug, imageUrl, type, resultPreview } = idea;
                    const typeSlug = type ? type.slug : 'discover-page-v1';
                    const topCompanies = resultPreview?.companies;
                    const totalNewCompanies =
                      resultPreview?.newCompaniesTotalHits ?? 0;
                    const companyCount = resultPreview?.totalHits ?? 0;
                    const snowflakeScores = parseSnowflakePointsFromRules(
                      resultPreview?.filterJson
                    );
                    const topCompaniesIconList =
                      getTopCompaniesIconList(topCompanies);
                    const totalCompaniesIcon =
                      topCompaniesIconList?.length ?? 0;
                    const totalCompanyCount = companyCount - totalCompaniesIcon;
                    const imageSrc = getCloudflareImageUrl({
                      src: getGridViewCoverFromImageUrl(imageUrl),
                      width: 276,
                      height: 188,
                      fit: 'crop',
                    });

                    const responsive = getResponsiveImage({
                      src: getGridViewCoverFromImageUrl(imageUrl),
                      breakpoints: [
                        { width: 276, height: 188, fit: 'crop' },
                        { width: 163, height: 90, fit: 'crop' },
                      ],
                    });

                    return (
                      <TileLink
                        key={idea.id}
                        noUnderline
                        inheritColor
                        to={{
                          type: getRouteType(typeSlug),
                          payload: {
                            id: idea.id,
                            name: slug,
                          },
                        }}
                        onClick={() => {
                          window.scrollTo({
                            top: 0,
                            left: 0,
                          });
                          trackEvent({
                            action: 'click',
                            subject: 'tile',
                            gridId: idea.id,
                            index: index,
                          });
                        }}
                      >
                        <Tile>
                          <Tile.Header>
                            <Box height="143px" overflow="hidden">
                              <img
                                src={imageSrc}
                                srcSet={responsive.srcSet}
                                width="100%"
                              />
                            </Box>
                          </Tile.Header>
                          <Grid
                            height="326px"
                            paddingTop="x13"
                            paddingLeft="x3"
                            paddingRight="x3"
                            templateColumns="auto 120px"
                            backgroundColor="phantom"
                            gap="8px"
                            alignItems="center"
                          >
                            <Box>
                              <Text typography="largeStrong">{name || ''}</Text>
                            </Box>
                            <Snowflake points={snowflakeScores}>
                              <Snowflake.Labels />
                            </Snowflake>
                          </Grid>
                          <Tile.Footer>
                            <Grid
                              templateColumns={`repeat(${totalCompaniesIcon}, 16px) max-content auto`}
                              gap="10px"
                              alignItems="center"
                              paddingBottom="x2"
                              paddingLeft="x3"
                            >
                              {topCompaniesIconList?.map((url) => {
                                return (
                                  <CompanyIcon
                                    key={url}
                                    website={url}
                                    size={20}
                                  />
                                );
                              })}
                              {totalCompanyCount > 0 && (
                                <Text typography="tiny">
                                  {`+${totalCompanyCount} companies`}
                                </Text>
                              )}
                              {totalNewCompanies > 0 && (
                                <Box>
                                  <Badge tone="attention">{`${totalNewCompanies} New`}</Badge>
                                </Box>
                              )}
                            </Grid>
                          </Tile.Footer>
                        </Tile>
                      </TileLink>
                    );
                  })}
                </Grid>
              </Box>
            );
          }}
        </ScrollLoader>
      </If>
      <If match={isFetching}>
        <LoadingSkeleton />
      </If>
    </>
  );
};

export default track(
  {
    type: 'track',
    subject: 'page',
    action: 'landed',
  },
  { dispatchOnMount: true }
)(ExploreIdeas) as typeof ExploreIdeas;
